/**
 * Asset codes: the human-facing identifier on the sticker.
 *
 * Format is prefix + zero-padded number, `AST-1000`. Configurable because every
 * company already has a convention and will not adopt ours.
 *
 * There are two prefixes and TWO counters, one per series, so each reads consecutively:
 * the eighth employee-owned device is `EMP-1007` however many company assets were
 * registered around it. The two series therefore hit the same numbers, and a code is
 * unique only because the prefixes differ - which is why the settings endpoint refuses
 * to let both be set to the same string, and why `asset.code` is uniquely indexed.
 *
 * The prefix is chosen at INSERT time and then fixed. An asset whose ownership is
 * corrected afterwards keeps the code it was given: the code is on a sticker, in a
 * spreadsheet and in every report already printed, and a system that renumbers assets
 * behind people is worse than one whose prefix is occasionally wrong.
 *
 * The NUMBER IS ALLOCATED BY THE DATABASE, in the same transaction as the insert -
 * see AssetsService.allocateCode. The prototype scanned the in-memory array for the
 * first unused number, which is a read-then-write race: two people adding an asset
 * at the same time both read 1007, both format AST-1007, and the second insert
 * either fails or (with no unique index) silently duplicates a sticker number.
 * Nothing here reads existing rows to decide the next code.
 */

export interface CodeFormat {
  prefix: string;
  digits: number;
}

export const DEFAULT_CODE_PREFIX = 'AST-';
/** Employee-owned kit. Configurable like the other one; the two only have to differ,
 *  which is the whole point of having a second. */
export const DEFAULT_EMPLOYEE_CODE_PREFIX = 'EMP-';
export const DEFAULT_CODE_DIGITS = 4;
/** Both counters start here. They move independently from that point on. */
export const DEFAULT_CODE_START = 1000;

/** Prefix rules kept deliberately tight: this ends up on a printed label and in a
 *  filename, so spaces and punctuation beyond `-` and `/` cause more trouble than
 *  they are worth. Length is the loose part: a prefix that spells the org out
 *  (`EMPASTINN00`) is longer than the four characters this shipped with. */
export const CODE_PREFIX_PATTERN = /^[A-Z0-9][A-Z0-9\-/]{0,19}$/;

/** Kept in step with the quantifier in the pattern above. */
export const MAX_CODE_PREFIX_LENGTH = 20;

export const MIN_CODE_DIGITS = 1;
export const MAX_CODE_DIGITS = 10;

export function isValidCodePrefix(prefix: string): boolean {
  return CODE_PREFIX_PATTERN.test(prefix);
}

/**
 * Render a sequence number as a code.
 *
 * A number wider than `digits` is NOT truncated - it simply renders longer. Padding
 * is cosmetic alignment; silently dropping a leading digit would mint a code that
 * collides with an existing one.
 */
export function formatAssetCode(sequence: number, format: CodeFormat): string {
  return `${format.prefix}${String(sequence).padStart(format.digits, '0')}`;
}

/**
 * Pull the numeric tail off a code, for sorting a register by sticker order rather
 * than by string. Returns null when the code does not end in digits.
 */
export function parseAssetCodeSequence(code: string, prefix: string): number | null {
  if (!code.startsWith(prefix)) return null;
  const tail = code.slice(prefix.length);
  if (!/^\d+$/.test(tail)) return null;
  return Number(tail);
}

/** Normalises a code typed by a human or pasted from a spreadsheet. Codes are
 *  compared and stored uppercase, so `ast-1000` finds `AST-1000`. */
export function normalizeAssetCode(code: string): string {
  return code.trim().toUpperCase().replace(/\s+/g, '');
}
